import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Sparkles,
  ScanLine,
  Palette,
  Gauge,
  Users,
  BookOpen,
  Settings,
  Search,
  LogOut,
  Sun,
  Moon,
  Menu,
  Building2,
  MessageSquare,
  WifiOff,
  Wand2,
  Library,
  Compass,
  UserCog,
  Coins,
  Gift,
  FileText,
  Compass as CompassIcon,
  TrendingUp,
  Briefcase,
  Building2 as BuildingIcon,
  Inbox as InboxIcon,
  Lock as LockIcon,
  Sparkles as GrowthIcon,
  LayoutGrid as PortfolioIcon,
  Presentation,
  Globe as GlobeIcon,
} from "lucide-react";
import Logo from "@/components/Logo";
import { useTheme } from "@/lib/theme-context";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useCommandPalette } from "@/components/CommandPalette";
import NotificationsBell from "@/components/NotificationsBell";
import { onOffline, onOnline } from "@/lib/pwa";
import { useAuth } from "@/lib/auth-context";

const NAV_ITEMS = [
  { to: "/dashboard",            label: "Dashboard",       icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/teach-from-anything",  label: "Teach from anything", icon: Wand2,       testid: "nav-tfa" },
  { to: "/lessons",              label: "Lessons",         icon: Library,         testid: "nav-lessons" },
  { to: "/explore",              label: "Explore",         icon: Compass,         testid: "nav-explore" },
  { to: "/library",              label: "Library",         icon: BookOpen,        testid: "nav-library" },
  { to: "/teaching-assistant",   label: "AI Teaching",     icon: Sparkles,        testid: "nav-teaching" },
  { to: "/ocr",                  label: "Document OCR",    icon: ScanLine,        testid: "nav-ocr" },
  { to: "/content-studio",       label: "Content Studio",  icon: Palette,         testid: "nav-content" },
  { to: "/assessment",           label: "Assessment",      icon: Gauge,           testid: "nav-assessment" },
  { to: "/attendance",           label: "Attendance",      icon: Users,           testid: "nav-attendance" },
  { to: "/parent-comms",         label: "Parent Comms",    icon: MessageSquare,   testid: "nav-parent-comms" },
  { to: "/knowledge-hub",        label: "Knowledge Hub",   icon: BookOpen,        testid: "nav-knowledge" },
  { to: "/institution",          label: "Institution",     icon: Building2,       testid: "nav-institution" },
  { to: "/profile",              label: "My profile",      icon: UserCog,         testid: "nav-profile" },
  { to: "/resume",               label: "AI Resume",       icon: FileText,        testid: "nav-resume" },
  { to: "/career",               label: "Career Copilot",  icon: CompassIcon,     testid: "nav-career" },
  { to: "/growth-studio",        label: "Growth Studio",   icon: GrowthIcon,      testid: "nav-growth" },
  { to: "/growth-studio/portfolio",     label: "My Portfolio",     icon: PortfolioIcon, testid: "nav-portfolio" },
  { to: "/growth-studio/demo-lesson",   label: "Demo Lesson Prep", icon: Presentation,  testid: "nav-demo" },
  { to: "/growth-studio/global-readiness", label: "Global Readiness", icon: GlobeIcon,  testid: "nav-readiness" },
  { to: "/inbox",                label: "Secure Inbox",    icon: LockIcon,        testid: "nav-inbox" },
  { to: "/jobs",                 label: "Jobs for educators", icon: Briefcase,     testid: "nav-jobs" },
  { to: "/hiring",               label: "Hiring Hub",      icon: BuildingIcon,    testid: "nav-hiring" },
  { to: "/cv-requests",          label: "CV requests",     icon: InboxIcon,       testid: "nav-cv-requests" },
  { to: "/wallet",               label: "TeeBee Coins",    icon: Coins,           testid: "nav-wallet" },
  { to: "/refer",                label: "Refer & Earn",    icon: Gift,            testid: "nav-refer" },
  { to: "/preferences",          label: "Preferences",     icon: UserCog,         testid: "nav-preferences" },
  { to: "/settings",             label: "Settings",        icon: Settings,        testid: "nav-settings" },
];

// Mobile bottom-nav: 5 most-used routes
const MOBILE_NAV = [
  { to: "/dashboard",            label: "Home",    icon: LayoutDashboard, testid: "mobnav-dashboard" },
  { to: "/teach-from-anything",  label: "Teach",   icon: Wand2,           testid: "mobnav-teach" },
  { to: "/library",              label: "Library", icon: BookOpen,        testid: "mobnav-library" },
  { to: "/explore",              label: "Explore", icon: Compass,         testid: "mobnav-explore" },
  { to: "/settings",             label: "More",    icon: Menu,            testid: "mobnav-more" },
];

function SidebarContent({ onNavigate }) {
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || !!user?.is_institution_admin;
  const items = isAdmin
    ? [...NAV_ITEMS.slice(0, 14), { to: "/admin", label: "Admin Analytics", icon: TrendingUp, testid: "nav-admin" }, ...NAV_ITEMS.slice(14)]
    : NAV_ITEMS;

  // Poll Secure Inbox unread count (every 30s)
  const [inboxUnread, setInboxUnread] = useState(0);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    let timer;
    const fetchUnread = async () => {
      try {
        const { default: api } = await import("@/lib/api");
        const { data } = await api.get("/connect/inbox/unread");
        if (alive) setInboxUnread(Number(data?.unread) || 0);
      } catch { /* ignore */ }
    };
    fetchUnread();
    timer = setInterval(fetchUnread, 30000);
    return () => { alive = false; clearInterval(timer); };
  }, [user]);
  return (
    <div className="h-full flex flex-col">
      <div className="px-5 pt-5 pb-3">
        <Logo size="md" />
      </div>
      <div className="px-3 py-1">
        <p className="px-2 py-1 text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
          Workspace
        </p>
      </div>
      <nav className="flex-1 px-3 space-y-0.5 overflow-auto tb-no-scrollbar">
        {items.map((item) => {
          const Active = location.pathname === item.to ||
            (item.to !== "/dashboard" && location.pathname.startsWith(item.to));
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              data-testid={item.testid}
              className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all
                ${Active
                  ? "bg-primary/10 text-foreground font-medium"
                  : "text-foreground/70 hover:bg-muted hover:text-foreground"}`}
            >
              <Icon className={`h-4 w-4 ${Active ? "text-primary" : ""}`} />
              <span>{item.label}</span>
              {item.to === "/inbox" && inboxUnread > 0 && (
                <span
                  className="ml-auto h-5 min-w-[20px] px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold grid place-items-center"
                  data-testid="nav-inbox-badge"
                >
                  {inboxUnread > 99 ? "99+" : inboxUnread}
                </span>
              )}
              {Active && item.to !== "/inbox" && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />}
              {Active && item.to === "/inbox" && inboxUnread === 0 && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 m-3 rounded-2xl bg-gradient-to-br from-primary/10 via-muted/40 to-primary/5 border border-border">
        <div className="flex items-center gap-2">
          <span className="h-6 w-6 rounded-full tb-ai-orb grid place-items-center">
            <Sparkles className="h-3 w-3 text-white" />
          </span>
          <p className="text-xs font-display font-medium">TeeBee credits</p>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">
          Unlimited generations on the beta plan.
        </p>
      </div>
    </div>
  );
}

export default function Layout({ children, title, anonymous = false }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { open: openCmd } = useCommandPalette();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    const off1 = onOnline(() => setOnline(true));
    const off2 = onOffline(() => setOnline(false));
    return () => { off1?.(); off2?.(); };
  }, []);

  // Close mobile drawer on route change + refresh balance
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Live coin balance — only for authenticated users
  useEffect(() => {
    if (anonymous) return;
    let alive = true;
    let timer;
    const fetchBalance = async () => {
      try {
        const api = (await import("@/lib/api")).default;
        const { data } = await api.get("/coins/wallet");
        if (alive) setBalance(data.balance);
      } catch { /* unauthenticated etc. */ }
    };
    fetchBalance();
    timer = setInterval(fetchBalance, 30000);
    return () => { alive = false; clearInterval(timer); };
  }, [location.pathname, anonymous]);

  const initials = (user?.name || "T")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen flex tb-dashboard-bg" data-testid="app-shell">
      {/* Desktop sidebar — hidden in anonymous mode */}
      {!anonymous && (
        <aside className="hidden lg:flex w-64 shrink-0 border-r border-border bg-card/60 backdrop-blur-xl flex-col">
          <SidebarContent />
        </aside>
      )}

      {/* Mobile drawer */}
      {!anonymous && mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm tb-fade-in" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-card border-r border-border tb-fade-up shadow-2xl">
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Top header */}
        <header className="sticky top-0 z-30 flex items-center gap-2 sm:gap-3 px-4 sm:px-6 lg:px-8 py-3 border-b border-border tb-glass">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden rounded-full"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-trigger"
            aria-label="Open menu"
            style={{ display: anonymous ? "none" : undefined }}
          >
            <Menu className="h-5 w-5" />
          </Button>

          {/* Logo for anonymous OR mobile */}
          {anonymous ? (
            <Link to="/" data-testid="anon-logo">
              <Logo size="sm" to={null} />
            </Link>
          ) : (
            <Link to="/dashboard" className="lg:hidden" data-testid="mobile-logo">
              <Logo size="sm" variant="minimal" to={null} />
            </Link>
          )}

          {/* Desktop page title */}
          <div className="hidden lg:block min-w-0">
            <h1 className="font-display text-lg sm:text-xl font-medium truncate" data-testid="page-title">
              {title || "Welcome"}
            </h1>
          </div>

          {!online && (
            <span
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200 px-3 py-1 text-[11px] font-medium"
              data-testid="offline-banner"
            >
              <WifiOff className="h-3 w-3" /> Offline
            </span>
          )}

          {/* Cmd-K search button */}
          {!anonymous && (
          <div className="flex-1 max-w-md mx-auto">
            <button
              onClick={openCmd}
              data-testid="open-command-palette"
              className="w-full inline-flex items-center gap-2 rounded-full px-3 sm:px-4 py-2 text-xs sm:text-sm bg-muted/60 hover:bg-muted text-muted-foreground transition border border-transparent hover:border-border"
            >
              <Search className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left truncate">Search anything…</span>
              <kbd className="hidden sm:inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[10px] font-mono bg-background">
                ⌘K
              </kbd>
            </button>
          </div>
          )}
          {anonymous && <div className="flex-1" />}

          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            className="rounded-full"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          {/* Coin balance pill — hidden for anonymous */}
          {!anonymous && (
          <button
            onClick={() => navigate("/wallet")}
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-500/30 hover:bg-amber-100 dark:hover:bg-amber-500/20 transition"
            data-testid="header-balance-pill"
            aria-label="TeeBee Coins balance"
          >
            <Coins className="h-3.5 w-3.5" />
            <span className="font-mono">{balance === null ? "—" : balance.toLocaleString()}</span>
          </button>
          )}

          {!anonymous && <NotificationsBell />}

          {anonymous ? (
            <div className="flex items-center gap-2" data-testid="anon-cta">
              <Button size="sm" variant="ghost" className="rounded-full" onClick={() => navigate("/login")} data-testid="anon-signin">
                Sign in
              </Button>
              <Button size="sm" className="rounded-full" onClick={() => navigate("/signup")} data-testid="anon-signup">
                Sign up free
              </Button>
            </div>
          ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 rounded-full pl-1 pr-1 sm:pr-3 py-1 hover:bg-muted transition"
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-8 w-8">
                  {user?.picture && <AvatarImage src={user.picture} alt={user.name} />}
                  <AvatarFallback className="bg-primary/15 text-primary text-xs font-medium">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:block text-sm font-medium">
                  {(user?.name || "Teacher").split(" ")[0]}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{user?.name}</span>
                  <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                <Settings className="h-4 w-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={async () => { await logout(); navigate("/login"); }}
                data-testid="menu-logout"
              >
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </header>

        {/* Content */}
        <div className="flex-1 px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-28 lg:pb-8 max-w-[1400px] w-full mx-auto">
          {/* Mobile page title (under header) */}
          <h1 className="lg:hidden font-display text-2xl font-medium tracking-tight mb-4" data-testid="page-title-mobile">
            {title || "Welcome"}
          </h1>
          {children}
        </div>

        {/* Mobile bottom nav — hidden for anonymous */}
        {!anonymous && (
        <nav
          className="lg:hidden fixed bottom-0 inset-x-0 z-30 tb-glass border-t border-border tb-safe-bottom"
          data-testid="mobile-bottom-nav"
        >
          <ul className="flex items-stretch justify-around">
            {MOBILE_NAV.map((item) => {
              const Active = item.to === "/settings"
                ? location.pathname === "/settings"
                : location.pathname === item.to || (item.to !== "/dashboard" && location.pathname.startsWith(item.to));
              const Icon = item.icon;
              return (
                <li key={item.to} className="flex-1">
                  <Link
                    to={item.to}
                    data-testid={item.testid}
                    className={`flex flex-col items-center justify-center gap-1 py-2 text-[11px] transition-colors ${
                      Active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                    {Active && <span className="absolute top-0 h-0.5 w-10 rounded-b-full bg-primary" />}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        )}
      </main>

      {/* BuddyBee — the SINGLE in-app AI assistant (mounted in App.js, not here) */}
    </div>
  );
}
